// ═══════════════════════════════════════
// Módulos — registro auto-extensible del sistema (Fase 2)
// ═══════════════════════════════════════
// Este archivo es la FUENTE DE VERDAD de los módulos que puede tener el portal.
// Cuando el código agrega un módulo nuevo (por ejemplo "dashboards" o "reuniones"),
// lo registramos acá y después hay que APRETAR "Sincronizar módulos" en /roles
// (`RolesPage.jsx:153`, gateado por admin) para que `sincronizarModulosConFirestore()`
// cree los docs que falten en la colección `modulos`.
//
// ⚠️ Esta cabecera decía "al arrancar la app corremos …" y era FALSO
// (report jUDTULA2uY8YuKXc6Z6f). El único llamador real es ese botón — más
// `window.__sincronizarModulos` (`:871`), el escape de consola. La afirmación
// se copió de acá a los tres `seed-permiso-*.cjs`, que es el molde que se usa
// para agregar permisos: el que la siguiera se saltaba un paso OBLIGATORIO y se
// quedaba mirando por qué el módulo no aparece en /permisos, con el catálogo
// declarado, el check de pares en verde y CI en verde. Todo dice que está bien
// y la pantalla está vacía — el peor tipo de falla para diagnosticar, porque no
// hay nada roto que buscar. El invariante lo fija ahora
// `scripts/modulos-codigo.guard.test.cjs`.
//
// Cada módulo define funciones (acciones que el módulo permite). El ABM de roles
// arma roles.permisos[] = { moduloCodigo, funcionCodigo, scope } donde scope es:
//   - 'todo'       → ve/opera sobre TODO lo de ese recurso
//   - 'propio'     → solo lo que creó
//   - 'asignado'   → lo que le asignaron explícitamente (ver asignaciones)
//   - 'compartido' → lo que participa (listas, threads, etc.)
// La evaluación vive en lib/permisos.js (puedeHacer).
//
// Es IDEMPOTENTE: si un módulo ya existe en Firestore, se respetan los cambios
// que el admin haya hecho en la UI (no se pisan `activo`, `orden`, `nombre`).
// Solo se actualizan funciones nuevas que hayan aparecido en el catálogo.
//
// Uso:
//   import { sincronizarModulosConFirestore, MODULOS_CATALOGO } from '@/lib/modulos'
//   await sincronizarModulosConFirestore()
// ═══════════════════════════════════════

import {
  collection, getDocs, addDoc, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
// #87 — cache de ejes en archivo aparte para romper la circular con
// accionesPermiso.js (ese archivo importa MODULOS_CATALOGO de acá).
import { setEjesCache, setEjeCache, ejeDeFallback } from './_ejesCache'

// Scopes disponibles para la columna "scope" del permiso.
//   - 'todo'         → todos los recursos visibles del módulo en su empresa de alcance.
//   - 'propio'       → solo los que el usuario creó (creadoPor === userId).
//   - 'asignado'     → solo los asignados actualmente al usuario (asignadoA === userId).
//   - 'involucrado'  → cualquier recurso donde el usuario aparece en participantesUserIds,
//                      aunque ya no sea el asignado actual. Útil para casos transferidos
//                      donde el creador original sigue interesado en la evolución.
//   - 'compartido'   → legacy. Equivalente a 'involucrado'. Se deprecará.
export const PERMISO_SCOPES = {
  TODO: 'todo',
  PROPIO: 'propio',
  ASIGNADO: 'asignado',
  INVOLUCRADO: 'involucrado',
  COMPARTIDO: 'compartido', // legacy, alias de INVOLUCRADO
}

// Catálogo canónico de módulos del sistema.
// Para agregar un módulo nuevo, agregá una entrada acá y redeploy.
// La función `sincronizarModulosConFirestore()` se encarga de crearlo en la DB.
//
// `naceApagada: true` en una función (report vxONAxDNRKyVcyAbIwTS, mesa del
// 19-oct-2026: «toda función nueva nace apagada»): la siembra de roles
// (`scripts/seed-roles-v2.js`) no la otorga a ningún rol; se tilda en
// /permisos. Una función nueva de un módulo operativo sin la marca pone rojo
// `src/core/lib/seedRolesV2.naceApagada.test.js`: o nace apagada, o se suma
// a la lista sembrada de ese test con la razón en el diff. La marca es del
// catálogo y no viaja a Firestore (ver `funcionParaFirestore`).
export const MODULOS_CATALOGO = [
  {
    codigo: 'empresas', // código INTACTO — solo cambió el nombre visible (6-jul)
    nombre: 'Mis empresas',
    descripcion: 'ABM de empresas cliente y sus datos diagnósticos.',
    orden: 10,
    funciones: [
      { codigo: 'ver',       nombre: 'Ver' },
      { codigo: 'crear',     nombre: 'Crear' },
      { codigo: 'editar',    nombre: 'Editar' },
      { codigo: 'eliminar',  nombre: 'Eliminar' },
      { codigo: 'cambiarEstado', nombre: 'Cambiar estado comercial' },
      // P1.4 (3-ago-2026) — separada de `editar` a propósito: editar el perfil
      // de una empresa y apagarle la sincronización no son la misma
      // responsabilidad, y se quiere poder dar una sin la otra. Gobierna el
      // callable `actualizarSyncEmpresa`: kill switch (pausa total) +
      // syncConfigOverride.
      // ⚠️ ACOTADA el 15-ago-2026: el callable exige `requireStaff` ADEMÁS de
      // este permiso. Acá decía que podía darse "a un owner cliente para que
      // pause la suya", y era falso como control: `empresas` es eje OPERATIVO,
      // así que el seed la reparte a los 10 roles no-Wizard — un OWNER podía
      // DESpausarse solo, con el mismo permiso. Hoy este par acota POR EMPRESA
      // dentro del staff; no habilita a un tenant a gobernar su gasto.
      { codigo: 'gobernarSync', nombre: 'Pausar/reanudar sincronización y overrides' },
    ],
  },
  {
    codigo: 'usuarios',
    nombre: 'Usuarios',
    descripcion: 'Gestión global de usuarios (catálogo de plataforma). Solo Operaciones/Wizard.',
    orden: 20,
    funciones: [
      { codigo: 'ver',      nombre: 'Ver' },
      { codigo: 'crear',    nombre: 'Crear' },
      { codigo: 'editar',   nombre: 'Editar' },
      { codigo: 'invitar',  nombre: 'Generar invitación' },
      { codigo: 'desactivar', nombre: 'Desactivar' },
      { codigo: 'cambiarRol', nombre: 'Cambiar rol' },
      { codigo: 'asignar',  nombre: 'Asignar empresa a usuario' },
    ],
  },
  {
    codigo: 'usuarios-empresa',
    nombre: 'Usuarios de empresa',
    descripcion: 'Meta-permisos del Owner: gestionar usuarios y asignaciones DE SU empresa.',
    orden: 22,
    funciones: [
      { codigo: 'ver',                nombre: 'Ver usuarios de la empresa' },
      { codigo: 'invitar',            nombre: 'Invitar usuario nuevo' },
      { codigo: 'asignar-rol',        nombre: 'Asignar rol (del catálogo predefinido)' },
      { codigo: 'remover',            nombre: 'Remover usuario de la empresa' },
      { codigo: 'invitar-consultor',  nombre: 'Invitar consultor externo a la empresa' },
    ],
  },
  {
    codigo: 'roles',
    nombre: 'Roles y permisos',
    descripcion: 'ABM de roles, permisos por módulo/función/scope.',
    orden: 25,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      { codigo: 'crear', nombre: 'Crear' },
      { codigo: 'editar', nombre: 'Editar' },
      { codigo: 'eliminar', nombre: 'Eliminar' },
    ],
  },
  {
    codigo: 'mensajes',
    nombre: 'Mensajes',
    descripcion: 'Conversaciones bilaterales (individuales, grupales, públicas, privadas).',
    orden: 30,
    funciones: [
      { codigo: 'ver',      nombre: 'Ver' },
      { codigo: 'enviar',   nombre: 'Enviar' },
      { codigo: 'responder', nombre: 'Responder' },
      { codigo: 'convertirEnTarea', nombre: 'Convertir en tarea' },
      { codigo: 'marcarLeido', nombre: 'Marcar leído' },
      // Bloque 3 Módulo Tutoriales (26-may-2026) — habilita conversaciones
      // internas Milbrands (empresaId: null) sin contexto de empresa. Sirve
      // a todo el sistema (DMs entre staff), no solo a tutoriales. Wizard +
      // Operaciones por default (asignar manual en /permisos post-deploy).
      { codigo: 'iniciar-interna', nombre: 'Iniciar conversación interna (sin empresa)' },
    ],
  },
  {
    codigo: 'accionables',
    nombre: 'Tareas',
    descripcion: 'Items de trabajo (Dato → Mensaje → Aceptación → Tarea). Escalado como transferencia.',
    orden: 40,
    funciones: [
      { codigo: 'ver',          nombre: 'Ver' },
      { codigo: 'crear',        nombre: 'Crear' },
      { codigo: 'editar',       nombre: 'Editar' },
      { codigo: 'aceptar',      nombre: 'Aceptar' },
      { codigo: 'rechazar',     nombre: 'Rechazar' },
      { codigo: 'escalar',      nombre: 'Escalar (transferir)' },
      { codigo: 'cerrar',       nombre: 'Cerrar' },
      { codigo: 'reabrir',      nombre: 'Reabrir' },
      // Jul 2026 — gatea el engranaje de la AccionableBar (mapeo sección→tipo
      // de tarea de los dashboards). Wizard/OPERACIONES pasan por el shortcut
      // de usePermisos; a otros roles se les siembra con
      // scripts/seed-permiso-configurar-mapeo.cjs.
      { codigo: 'configurarMapeo', nombre: 'Configurar tipo de tarea por sección (dashboards)' },
    ],
  },
  {
    codigo: 'casos',
    nombre: 'Casos',
    descripcion: 'Expedientes investigativos (reclamos, devoluciones, consultas). State machine + multi-SLA + portal externo.',
    orden: 45,
    funciones: [
      { codigo: 'ver',                          nombre: 'Ver' },
      { codigo: 'crear',                        nombre: 'Crear' },
      { codigo: 'editar',                       nombre: 'Editar metadatos' },
      { codigo: 'comentar-publico',             nombre: 'Comentar (visible al externo)' },
      { codigo: 'comentar-interno',             nombre: 'Comentar (solo equipo)' },
      { codigo: 'adjuntar',                     nombre: 'Adjuntar archivos' },
      { codigo: 'ver-adjuntos-confidenciales',  nombre: 'Ver adjuntos confidenciales' },
      { codigo: 'cerrar',                       nombre: 'Cerrar' },
      { codigo: 'reabrir',                      nombre: 'Reabrir' },
      { codigo: 'escalar',                      nombre: 'Escalar a supervisor' },
      { codigo: 'transferir',                   nombre: 'Transferir asignado' },
      { codigo: 'aprobar',                      nombre: 'Aprobar (si requiere aprobación)' },
      { codigo: 'eliminar',                     nombre: 'Eliminar (soft delete)' },
    ],
  },
  {
    codigo: 'asignaciones',
    nombre: 'Asignaciones',
    descripcion: 'Vínculos persona-empresa (define alcance: qué empresas ve cada usuario). Source of truth del modelo multi-tenant.',
    orden: 50,
    funciones: [
      { codigo: 'ver',      nombre: 'Ver asignaciones' },
      { codigo: 'crear',    nombre: 'Crear asignación' },
      { codigo: 'editar',   nombre: 'Editar asignación' },
      { codigo: 'eliminar', nombre: 'Eliminar asignación' },
    ],
  },
  {
    codigo: 'slas',
    nombre: 'SLAs',
    descripcion: 'Niveles de servicio con alertas automáticas al supervisor.',
    orden: 60,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      { codigo: 'crear', nombre: 'Crear' },
      { codigo: 'editar', nombre: 'Editar' },
      { codigo: 'eliminar', nombre: 'Eliminar' },
    ],
  },
  {
    codigo: 'notificaciones',
    nombre: 'Notificaciones',
    descripcion: 'Notificaciones de campana (alertas SLA, mensajes, etc.).',
    orden: 70,
    funciones: [
      { codigo: 'ver',         nombre: 'Ver' },
      { codigo: 'marcarLeido', nombre: 'Marcar leído' },
    ],
  },
  {
    codigo: 'dashboards',
    nombre: 'Dashboards',
    descripcion: 'Tableros de indicadores por empresa / consultor / global.',
    orden: 80,
    funciones: [
      { codigo: 'ver',      nombre: 'Ver' },
      { codigo: 'exportar', nombre: 'Exportar' },
    ],
  },
  {
    codigo: 'reuniones',
    nombre: 'Reuniones',
    descripcion: 'Agenda de reuniones, minutas, seguimientos.',
    orden: 85,
    funciones: [
      { codigo: 'ver',       nombre: 'Ver' },
      { codigo: 'crear',     nombre: 'Crear' },
      { codigo: 'editar',    nombre: 'Editar' },
      { codigo: 'eliminar',  nombre: 'Eliminar' },
    ],
  },
  {
    codigo: 'warnings',
    nombre: 'Warnings',
    descripcion: 'Alertas críticas por empresa, con criticidad y seguimiento.',
    orden: 90,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      { codigo: 'crear', nombre: 'Crear' },
      { codigo: 'gestionar', nombre: 'Gestionar' },
      { codigo: 'cerrar', nombre: 'Cerrar' },
    ],
  },
  {
    codigo: 'biblioteca',
    nombre: 'Biblioteca',
    descripcion: 'Documentos, adjuntos, protocolos compartidos.',
    orden: 100,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      { codigo: 'subir', nombre: 'Subir' },
      { codigo: 'eliminar', nombre: 'Eliminar' },
    ],
  },
  // ── Módulos de integración / comercial (sumados en #58) ──────────────
  // Eran app-módulos desde Fase 2/3 pero nunca entraron al catálogo de
  // permisos v2. Al sumarlos acá un rol puede concederlos como a cualquier
  // otro módulo, y `useModulosVisibles` deriva su visibilidad del rol.
  {
    codigo: 'mercadolibre',
    nombre: 'Mercado Libre',
    descripcion: 'Dashboards, métricas e integración OAuth con Mercado Libre.',
    orden: 110,
    funciones: [
      { codigo: 'ver',         nombre: 'Ver' },
      { codigo: 'sincronizar', nombre: 'Sincronizar' },
      { codigo: 'configurar',  nombre: 'Configurar integración' },
      // Capa de auditoría del dashboard (totalizadores cruzados + export CSV/JSON
      // de diferencias). Wizard/Operaciones la ven por default (short-circuit en
      // usePermisos); el cliente final NO, salvo que se le asigne en /permisos.
      // Sin defaults hardcoded — tras "Sincronizar módulos", tildar en /permisos.
      { codigo: 'auditoria',   nombre: 'Ver auditoría y exportar (debug)', naceApagada: true },
      // ADR 077 §3 bis (11-ago-2026) — el REPROCESO DELIBERADO de las fases
      // derivadas de IA (`iniciarSyncJob({ tipo: '…-fases2plus'|'…-fases234',
      // forzar: true })`). Separada de `sincronizar` a propósito, mismo criterio
      // que `empresas.gobernarSync` con `empresas.editar`: sincronizar trae
      // datos, esto REHACE inferencia YA PAGADA —la fase 3 no tiene tope y es
      // el 77 % de la factura de IA—, y no se quiere que cualquiera que
      // sincroniza pueda gastar de más. Sin defaults hardcoded: se tilda en
      // /permisos (Wizard pasa por el short-circuit de `puedeHacerServer`;
      // Operaciones NO — server-side no tiene atajo por staff).
      { codigo: 'reprocesarIA', nombre: 'Reprocesar fases de IA (rehace inferencia ya pagada)', naceApagada: true },
      // `calificarIdeas` (calificar ideas de contenido) se retiró con su
      // módulo (report LqCuiLJOzN0ZcHCzjZLV): un par que no gatea nada sería
      // una casilla de /permisos que miente. Si el rediseño vuelve a calificar
      // ideas, el par vuelve a nacer apagado, como reprocesarIA.
    ],
  },
  {
    codigo: 'tienda-nube', // slug canónico único (ADR 2026-08-13, decisión 1) — el catálogo admite guión ('audit-logs')
    nombre: 'Tienda Nube',
    descripcion: 'Dashboards y vistas del canal Tienda Nube (F2-T3, ADR 2026-08-16). Conectar/desconectar usa el módulo agnóstico integraciones.',
    orden: 21.5,
    funciones: [
      { codigo: 'ver', nombre: 'Ver dashboards y datos del canal' },
      // T4a (ADR 2026-08-16, enmienda T4) — espejo de `mercadolibre.sincronizar`:
      // el camino manual del modelo de sincro pide el permiso del módulo del
      // CANAL (iniciarSyncJob resuelve por plataformaId, fail-closed). Sin esta
      // entry, ningún rol puede disparar un sync TN a mano.
      { codigo: 'sincronizar', nombre: 'Sincronizar datos del canal (manual)' },
    ],
  },
  {
    // 14-ago-2026 (tajada OAuth §4.1, 2º canal) — GESTIÓN DE CONEXIONES, agnóstica
    // de canal. Nace porque 9 callables pedían permiso de Mercado Libre para operar
    // sobre CUALQUIER canal: reciben `plataformaId` y resuelven el adapter de un
    // registry. Con el literal viejo, una conexión de Tienda Nube daba
    // `permission-denied` a TODOS —incluido el dueño de la empresa— y, por no ser
    // un literal de los que `check-no-hardcode-gating.sh` mira, el candado no lo veía.
    //
    // Qué migró (8 de 9):
    //   · `configurar` — los 6 de `functions/conexiones.js`: conectar, link público,
    //     eliminar, desconectar, token manual, circuit breaker.
    //   · `ver` — `conexiones.listConexiones` y `usage/getSincroEstadoEmpresa`, que
    //     recibe `plataforma` como parámetro explícito.
    //
    // EL 9no CERRÓ EN T4a (19-ago-2026, enmienda T4 del ADR 2026-08-16), con una
    // resolución DISTINTA a la prometida acá: `syncJobs.getSyncJobStatus` (y también
    // `iniciarSyncJob`) no migraron al módulo agnóstico sino al permiso del módulo
    // del CANAL del job, resuelto por `plataformaId` con mapa fail-closed
    // (`functions/syncCore/plataformasPermisos.js`) — sincronizar/ver un sync ES por
    // canal, a diferencia de conectar/desconectar. Tests en
    // `functions/syncJobs.permisoPorCanal.test.js` (callable ejecutada, molde
    // reprocesoIA — el problema de doble instancia de ADR 069 §13 que trababa esto
    // se evitó con ese molde, sin emulator).
    //
    // Qué NO migró, a propósito: `mercadolibre.configurar` sigue vivo y correcto en
    // los tres config de dashboards de ML (`configRentabilidad`, `configProspectiva`,
    // `configPublicidad`), y `mercadolibre.ver` sigue siendo el permiso de VER el
    // vertical de ML (`config.js:54` lo declara como `permisoModulos`). Ninguno de
    // los dos se borra del catálogo: siguen teniendo consumidores reales.
    //
    // La migración de los roles existentes la hace `scripts/seed-permiso-integraciones.cjs`.
    // Sin correrlo, quien administraba conexiones pierde los 9 callables — y como el
    // frontend NO gatea con la matriz, no desaparece ningún botón: se rompe al clickear.
    codigo: 'integraciones',
    nombre: 'Integraciones',
    descripcion: 'Conectar, desconectar y administrar las cuentas de cualquier canal (Mercado Libre, Tienda Nube, …).',
    orden: 111,
    funciones: [
      { codigo: 'ver',        nombre: 'Ver conexiones' },
      { codigo: 'configurar', nombre: 'Conectar y administrar cuentas' },
    ],
  },
  {
    // 4-ago-2026 (plan Salud del Catalogo T0): Conocimiento = inteligencia del
    // ecosistema (agregados cross-cartera). Sus puertas piden STAFF antes del
    // par: tildárselo a un rol de cliente no lo habilita. Dentro del staff decide
    // la matriz (en el servidor el único atajo es Wizard): /permisos o
    // seed-permiso-conocimiento-ver. Abrirlo a sellers va por PLAN — trigger
    // anotado en docs/PLAN_conocimiento-salud-catalogo.md §4.
    // Eje `plataforma` y sólo staff desde el 31-oct-2026 (Mariano, report
    // LVyJbRGvZebhNU2MmO4G; del 24 al 31-oct fue `operativo`): la siembra ya no
    // le da el par a los roles de cliente, y el staff lo conserva entero
    // (`MODULOS_SOLO_STAFF`, `_ejesCache.js`).
    codigo: 'conocimiento',
    nombre: 'Conocimiento',
    descripcion: 'Inteligencia del ecosistema: costos ML, salud del catálogo (reclamos/devoluciones, preguntas vs ventas). Agregados cross-cartera.',
    orden: 116,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      // 22-sep-2026: el recálculo manual dejó de ser `requireStaff` a secas y
      // pasó a ser staff ∧ permiso — el callable exige los DOS (mismo molde que
      // `empresas.gobernarSync`). Tildarle este par a un rol de cliente NO lo
      // habilita: `requireStaff` sigue siendo el piso, porque el runner escribe
      // el doc de todas las empresas de la cartera.
      { codigo: 'recalcular', nombre: 'Recalcular a mano' },
    ],
  },
  {
    codigo: 'mercado',
    // 6Xp3M0ZN (5-jul): el nombre tiene que coincidir con lo que el usuario VE
    // en el sidebar ("Explorador de mercado") — con "Mercado (inteligencia)"
    // Mariano no lo encontraba en /permisos. El CÓDIGO no cambia (cero migración).
    nombre: 'Explorador de mercado',
    descripcion: 'Explorador de inteligencia de mercado (Market Pro / Real Trends). El staff lo usa en toda empresa; el cliente, sólo con el plan Mercado contratado.',
    orden: 115,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      // 3LRr3gQH (4-jul): ABM de identidades de vendedores (alias ofuscado →
      // nombre real) + toggle global. El backend lo exige en las mutaciones
      // del callable identidadVendedoresMercado (constraints §1.B).
      { codigo: 'identificar', nombre: 'Identificar vendedores (alias → nombre real)' },
      // ADR 047 (8-jul): dashboard "Mi Posición" — posición competitiva del
      // seller vs categoría (rollup __posicion + observación VictorIA). El
      // backend lo exige en el callable que sirve el rollup (constraints §1.B).
      // La CARD del dashboard se gatea aparte por vistasEmpresa (ADR 046);
      // este permiso gatea el DATO (lectura del cruce con Market Pro).
      { codigo: 'posicion', nombre: 'Ver Mi Posición (seller vs categoría)' },
      // ADR 2026-08-29 (estudios): generar y consultar estudios congelados de
      // mercado. El backend lo exige en generar/listar/ver (constraints §1.B),
      // siempre CON recurso {empresaId} para que el alcance ate (PR B).
      { codigo: 'estudios', nombre: 'Estudios de mercado (generar y ver)' },
      // ADR 2026-09-01 (packs y combos): descartar una recomendación del
      // dashboard. El backend lo exige en `combosDescartarRecomendacion`
      // (constraints §1.B), CON recurso {empresaId}. Nace SIN permiso por
      // default en ningún rol (regla 28-ago: toda capacidad se gobierna
      // desde /permisos) — lo tilda Mariano.
      { codigo: 'combos', nombre: 'Packs y combos (descartar recomendaciones)', naceApagada: true },
    ],
  },
  {
    codigo: 'productos',
    nombre: 'Productos',
    descripcion: 'Catálogo maestro de productos del seller + sourcing.',
    orden: 120,
    funciones: [
      { codigo: 'ver',      nombre: 'Ver' },
      { codigo: 'crear',    nombre: 'Crear' },
      { codigo: 'editar',   nombre: 'Editar' },
      { codigo: 'eliminar', nombre: 'Eliminar' },
    ],
  },
  {
    codigo: 'rentabilidad',
    nombre: 'Rentabilidad',
    descripcion: 'Simulador de costos por vender y análisis de márgenes.',
    orden: 130,
    funciones: [
      { codigo: 'ver',     nombre: 'Ver' },
      { codigo: 'simular', nombre: 'Simular costos' },
      // Separada de `simular` a propósito, mismo criterio que
      // `mercadolibre.reprocesarIA` (`:289-298`): `simular` calcula lo que
      // falta —barato, incremental—, y esto PISA todas las órdenes del rango
      // ignorando estado previo (`functions/rentabilidad.js:27-28`), o sea que
      // rehace la cascada de 3 capas ya pagada. No se quiere que cualquiera que
      // simula pueda gastar de más. §1.F (Mariano, 28-ago-2026): la capacidad
      // nueva nace como par en el catálogo y la decide la matriz, no un
      // predicado en código.
      //
      // SIN piso `requireStaff`, al revés que `empresas.gobernarSync` (`:83-89`)
      // y `observabilidad.correr-drill-alertas` (`:686-691`): esos son palancas
      // de la plataforma sobre un tenant, y el forzado es una capacidad DEL
      // cliente — `functions/rentabilidad.js:31-32` lo dice desde que nació
      // ("puede usarlo el cliente final cuando habilitemos el botón"). El molde
      // es el que Mariano corrigió a mano: "no es por staff, es por permisos"
      // (ADR 083 §4), que dejó `reprocesarIA` sin piso.
      //
      // `rentabilidad` es eje operativo (`_ejesCache.js:60`), así que la
      // siembra de roles se lo repartía a los 10 roles no-Wizard. Nace apagado
      // (`naceApagada`, report vxONAxDNRKyVcyAbIwTS): la siembra no lo otorga,
      // igual que `reprocesarIA`; se tilda en /permisos.
      { codigo: 'recalcularForzado', nombre: 'Recalcular forzado (pisa todas las órdenes del rango, rehace la cascada)', naceApagada: true },
    ],
  },
  {
    codigo: 'diagnostico',
    nombre: 'Diagnóstico',
    descripcion: 'Framework de evaluación: problemáticas, dimensiones, criticidad.',
    orden: 140,
    funciones: [
      { codigo: 'ver',    nombre: 'Ver' },
      { codigo: 'crear',  nombre: 'Crear' },
      { codigo: 'editar', nombre: 'Editar' },
    ],
  },
  {
    codigo: 'consultoria',
    nombre: 'Consultoría',
    descripcion: 'Marketplace de consultores certificados (Fase 3).',
    orden: 150,
    funciones: [
      { codigo: 'ver',       nombre: 'Ver' },
      { codigo: 'contratar', nombre: 'Contratar' },
    ],
  },
  {
    codigo: 'postventa',
    nombre: 'Postventa',
    descripcion: 'Dashboard post-venta ML: reputación, reclamos, devoluciones + tareas estructurales + tab Patrones (Casos). MVP mayo 2026.',
    orden: 155,
    funciones: [
      { codigo: 'ver',                  nombre: 'Ver dashboard postventa' },
      { codigo: 'ejecutar_agente',      nombre: 'Disparar agente manual' },
      { codigo: 'configurar_agente',    nombre: 'Configurar cron del agente' },
      { codigo: 'crear_tarea',          nombre: 'Crear tarea desde sección' },
      { codigo: 'abrir_caso',           nombre: 'Abrir caso desde tab Patrones' },
      { codigo: 'editar_nombre_agente', nombre: 'Renombrar agente' },
    ],
  },
  {
    codigo: 'configuracion',
    nombre: 'Configuración',
    descripcion: 'ABM de catálogos globales (rubros, canales, categorías, etc.).',
    orden: 1000,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      { codigo: 'editar', nombre: 'Editar' },
    ],
  },
  // ════════════════════════════════════════════════════════════════════════
  // v2 (#87) — Items del sidebar como módulos individuales del catálogo.
  // Cada item del sidebar tiene su propio módulo para que el admin pueda
  // gobernar item-por-item desde la matriz Permisos. Eje editable desde la
  // UI "Items del sidebar" (sección Usuarios y Permisos).
  // ════════════════════════════════════════════════════════════════════════

  // ── Operativos (los ven staff y según corresponda cliente) ──
  {
    codigo: 'señales',
    nombre: 'Señales',
    descripcion: 'Detección automática de oportunidades/problemas → genera tareas.',
    orden: 41,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'configurar', nombre: 'Configurar' }],
  },
  {
    codigo: 'calendario',
    nombre: 'Calendario',
    descripcion: 'Vista calendario de tareas, reuniones, vencimientos.',
    orden: 42,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }],
  },
  {
    codigo: 'metricas',
    nombre: 'Métricas',
    descripcion: 'KPIs operativos del módulo Tareas (lead time, throughput, etc.).',
    orden: 43,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'exportar', nombre: 'Exportar' }],
  },
  {
    codigo: 'difusiones',
    nombre: 'Difusiones',
    descripcion: 'Comunicaciones broadcast a usuarios/empresas. Una-a-muchas.',
    orden: 31,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'enviar', nombre: 'Enviar' }],
  },
  {
    codigo: 'reportes',
    nombre: 'Reportes',
    descripcion: 'Reportes globales de la plataforma.',
    orden: 81,
    // `editar` (13-ago-2026, ítem 6h) — NO es una acción nueva: es la que el
    // código ya consultaba sin declarar. `ReportesPage.jsx:117` hace
    // `puedeEditar('reportes')` y con eso gatea los tres botones de escritura de
    // /reportes (crear en :344, editar y borrar en :418). Al no estar en el
    // catálogo, el par `reportes.editar` no se podía tildar en /permisos: el
    // gate era INASIGNABLE y sólo lo pasaban Wizard e isAdmin, que saltean la
    // matriz — la misma rigidez que §1.B le reprocha al hardcode.
    // Se declara `editar` y no se parte en crear/editar/eliminar porque el
    // código consulta UN par: partirlo dejaría dos entradas declaradas sin
    // consumidor. Es el mismo patrón que `empresas` (`EmpresasPage.jsx:146`:
    // `puedeEditar('empresas')` como gate de escritura de la página).
    // No cambia el comportamiento de nadie: declarar hace el permiso
    // ASIGNABLE, no lo asigna. Quien no lo tenga tildado sigue sin poder.
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      { codigo: 'editar', nombre: 'Editar' },
      { codigo: 'exportar', nombre: 'Exportar' },
    ],
  },
  {
    codigo: 'protocolos',
    nombre: 'Protocolos',
    descripcion: 'Protocolos de acción y procedimientos operativos.',
    orden: 101,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },

  // ── Plataforma (Wizard / Operaciones — admin del sistema) ──
  {
    codigo: 'frameworks',
    nombre: 'Frameworks',
    descripcion: 'Catálogo de frameworks de evaluación (diagnóstico, casos, etc.).',
    orden: 200,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'dimensiones',
    nombre: 'Dimensiones',
    descripcion: 'Dimensiones de evaluación (ABM global).',
    orden: 201,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'motor-criticidad',
    nombre: 'Motor criticidad',
    descripcion: 'Reglas y parámetros del motor de criticidad.',
    orden: 202,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'reglas',
    nombre: 'Reglas de negocio',
    descripcion: 'Motor de reglas que crea tareas/alertas automáticas.',
    orden: 203,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'prompts-ia',
    nombre: 'Prompts IA',
    descripcion: 'Catálogo de prompts del sistema (IA generativa).',
    orden: 204,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'organigrama',
    nombre: 'Organigrama',
    descripcion: 'Árbol de relaciones reportaA entre usuarios.',
    orden: 21,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'auditoria-permisos',
    nombre: 'Auditoría de permisos',
    descripcion: 'Log inmutable de cambios al modelo de permisos.',
    orden: 26,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }],
  },
  {
    codigo: 'modulos-plataforma',
    nombre: 'Módulos de plataforma',
    descripcion: 'ABM de módulos del sidebar y su clasificación por eje.',
    orden: 210,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'planes',
    nombre: 'Planes',
    descripcion: 'ABM de planes de suscripción.',
    orden: 211,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'plataformas',
    nombre: 'Plataformas',
    descripcion: 'Catálogo central de plataformas: visibilidad de producto (activa) y seed; interruptores de funciones de todas las empresas.',
    orden: 212,
    funciones: [
      // `administrar` = prender/apagar la visibilidad de un canal
      // (setPlataformaActiva) y sembrar el catálogo (seedPlataformas). El
      // backend lo exige como PAR con piso staff (plataformasAdmin.js, OPS-2
      // PR-2): tildar esta tupla a un rol cliente NO le abre la puerta.
      { codigo: 'administrar', nombre: 'Administrar' },
      // `interruptores` = la palanca de feature flags (featureFlagsAdmin.js:
      // listar y mover el global de todas las empresas y sus excepciones).
      // Hasta el 29-oct-2026 la abría `administrar`; Mariano la separó (report
      // LVyJbRGvZebhNU2MmO4G): permiso propio, sólo staff — mismo par con piso
      // staff. Nace apagada: la siembra no se la da a ningún rol.
      { codigo: 'interruptores', nombre: 'Mover los interruptores de funciones de todas las empresas', naceApagada: true },
    ],
  },
  {
    codigo: 'ia-config',
    nombre: 'IA — Configuración',
    descripcion: 'Configuración del proxy IA, modelos, API keys.',
    orden: 220,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'auto-meeting',
    nombre: 'Auto-meeting',
    descripcion: 'Tipos de reunión + prompts del análisis IA de transcripciones.',
    orden: 222,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'api-terceros',
    nombre: 'APIs terceros',
    descripcion: 'Conexiones OAuth con plataformas externas (ML, Google, etc.).',
    orden: 221,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    codigo: 'agentes',
    nombre: 'Agentes IA',
    descripcion: 'ABM de agentes IA (catálogo + configuración por empresa).',
    orden: 222,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }, { codigo: 'editar', nombre: 'Editar' }],
  },
  {
    // Plan IA Paso 9 — capa de calificación y aprendizaje de la IA. Sus cinco
    // funciones se asignan por rol en /permisos, y del lado del servidor sus puertas
    // piden además staff (#4019, #4021): calificar es la amplia (👍/👎); curar/aprobar, acotadas.
    codigo: 'ia-aprendizaje',
    nombre: 'IA — Aprendizaje',
    descripcion: 'Calificación 👍/👎 de los outputs de IA, curación del corpus y tablero de aprendizaje.',
    orden: 224,
    funciones: [
      { codigo: 'ver',       nombre: 'Ver' },
      { codigo: 'calificar', nombre: 'Calificar' },
      // ADR 084: quién ENSEÑA sin pasar por curación (el "+" del feedback).
      // Sin este permiso, la observación nace pendiente y la aprueba un curador.
      { codigo: 'ensenar-directo', nombre: 'Enseñar directo (sin curación)' },
      { codigo: 'curar',     nombre: 'Curar' },
      { codigo: 'aprobar',   nombre: 'Aprobar' },
    ],
  },
  {
    codigo: 'observabilidad',
    nombre: 'Observabilidad',
    descripcion: 'Panel admin: salud del sistema, syncJobs, costos, alertas.',
    orden: 230,
    funciones: [
      { codigo: 'ver', nombre: 'Ver' },
      // 28-may-2026 (sesión noche, Bloque 8 — ML Probe): permite ejecutar GETs
      // ad-hoc a la API de ML desde la tab "🔬 ML Probe" para diagnosticar
      // endpoints sin necesidad de scripts node. mlSafeFetch ya garantiza
      // que solo se ejecuten GETs (NON-GET bloqueado estructuralmente).
      { codigo: 'probar-endpoints-ml', nombre: 'Probar endpoints ML (debug)' },
      // 1-sep-2026 — el par lo EXIGE `functions/usage/watchdogDrill.js:195`
      // y no estaba declarado acá, así que era INASIGNABLE: la única puerta era
      // el short-circuit del Wizard (`functions/permisos.js:534`). Mientras tanto
      // tres lugares instruían tildarlo — `AlertasTab.jsx` ("se tilda en /roles"),
      // `PLAN_ACTUAL.md` y `runbooks/detector-estado-trabado.md` ("lo corre Mariano
      // O QUIEN TENGA este permiso") — y esa segunda mitad era imposible. No se
      // notó porque Mariano es Wizard y siempre pasó por el short-circuit.
      //
      // El permiso NO reemplaza al piso staff: `requireStaff` corre ANTES
      // (`watchdogDrill.js:192`), así que tildárselo a un rol cliente deja
      // una casilla inerte. Mismo par-con-piso-staff que `plataformas.administrar`
      // y `mercadolibre.reprocesarIA`.
      { codigo: 'correr-drill-alertas', nombre: 'Correr el simulacro del watchdog (emite alertas reales y manda mails)' },
    ],
  },
  {
    // ADR 051 — triage gate del bug tracker interno, 100% por permisos (nada por
    // rol ni por flag). El rol que tenga estas capacidades las puede hacer.
    // 11-ago-2026 (decisión Mariano): se ELIMINA `crearAprobado` — todos los
    // reportes nacen 'informe' sin excepción y la única puerta a 'nuevo' es
    // `autorizar`. El permiso tildado en un rol hacía que esos reportes
    // saltearan el triage y entraran solos a la cola del agente nocturno.
    // (Puede quedar referenciado en docs de roles viejos: es inerte — el
    // server ya no lo consulta.)
    codigo: 'debugging',
    nombre: 'Debugging',
    descripcion: 'Bug tracker interno. Todos los reportes nacen informados; autorizar es la única puerta a la cola (ADR 051, decisión 11-ago-2026).',
    orden: 235,
    funciones: [
      { codigo: 'ver',       nombre: 'Ver' },
      { codigo: 'crear',     nombre: 'Generar bug informado (requiere aprobación)' },
      { codigo: 'autorizar', nombre: 'Autorizar / rechazar informes' },
    ],
  },
  {
    codigo: 'audit-logs',
    nombre: 'Audit logs',
    descripcion: 'Vista global de eventos críticos auditados (compliance).',
    orden: 231,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }],
  },
  {
    // Instalaciones self-service (tajada 1 post PR 5c-0). Gateado por PERMISO,
    // no por hardcode de rol: Wizard pasa por el shortcut del server y
    // OPERACIONES **no** (su bypass es sólo del front — ver `usePermisos.js`,
    // "caso Mateo"), así que el rol Operaciones se siembra explícito. Cualquier
    // otro rol se tilda en /permisos sin tocar código.
    // Es el MISMO código que gatea el callable `listarInstalacionesSelfService`:
    // el ítem del sidebar y la lectura tienen que mirar el mismo módulo, o pasa
    // lo de Debugging (la entrada se ve y el server la rechaza).
    codigo: 'instalaciones',
    nombre: 'Instalaciones Tienda Nube',
    descripcion: 'Las tiendas que intentaron instalar la app desde el catálogo de Tienda Nube, terminen o no. Dato interno de Milbrands.',
    orden: 232,
    funciones: [{ codigo: 'ver', nombre: 'Ver' }],
  },

  // ── Tutoriales (Bloque 1 — 26-may-2026) ───────────────────────────────────
  // Capacitación interna: videos grabados in-app (pantalla + mic + cámara
  // opcional como burbuja overlay tipo Loom). Archivos en Firebase Storage.
  // Sólo staff (Mariano, 28-sep-2026; report LVyJbRGvZebhNU2MmO4G): eje `plataforma`
  // y en `MODULOS_SOLO_STAFF` (`_ejesCache.js`) — la siembra se lo da entero a los
  // roles de staff y a ningún otro; las puertas del server piden staff antes del par.
  {
    codigo: 'tutoriales',
    nombre: 'Tutoriales',
    descripcion: 'Capacitación interna — videos cortos sobre cómo usar la plataforma.',
    orden: 35,
    funciones: [
      { codigo: 'ver',      nombre: 'Ver listado y reproducir' },
      { codigo: 'grabar',   nombre: 'Grabar, publicar, editar y pausar lo propio' },
      { codigo: 'eliminar', nombre: 'Eliminar, editar y pausar tutoriales ajenos' },
    ],
  },

  // ── Club Milbrands (20-ago-2026) ──────────────────────────────────────────
  // `club` es el GRUPO del sidebar (mismo rol que milbrandsOperaciones: sin
  // rutas propias, solo agrupa). `formacion` es el vertical real de la ola 1.
  // Los otros tres módulos del Club (comunidad, eventos, empleo) se declaran
  // en SU ola, no antes: una entrada de catálogo sin módulo detrás es código
  // muerto declarativo (hallazgo de code-reviewer en la compuerta 2).
  // Sin defaults: tras `Sincronizar módulos`, Mariano tilda en `/permisos`.
  {
    codigo: 'club',
    nombre: 'Club Milbrands',
    descripcion: 'Grupo del sidebar que agrupa los módulos del Club (Formación, y luego Comunidad, Eventos, Empleo).',
    orden: 98,
    funciones: [
      { codigo: 'ver', nombre: 'Ver el grupo y sus items' },
    ],
  },
  {
    codigo: 'formacion',
    nombre: 'Formación',
    descripcion: 'Cursos y tracks de video del Club: catálogo, progreso del miembro y gestión de contenido.',
    orden: 99,
    funciones: [
      { codigo: 'ver',                 nombre: 'Ver el catálogo y reproducir lo habilitado' },
      { codigo: 'gestionar-contenido', nombre: 'Crear, editar y publicar cursos, tracks y lecciones' },
      { codigo: 'programar',           nombre: 'Definir tema del mes y destacados (vigencias)' },
      { codigo: 'gestionar-acceso',    nombre: 'Otorgar y quitar acceso de usuarios a contenido' },
      { codigo: 'ver-estadisticas',    nombre: 'Ver estadísticas de consumo del contenido propio' },
      // 22-ago-2026 (tuerquita de videos — decisión de Mariano): la limpieza de
      // Bunny NO vive en /api-terceros (ahí están los switches de sincro y otro
      // equipo podría apagar una sincro por error) — vive acá, con su permiso.
      { codigo: 'gestionar-videos',    nombre: 'Limpieza de videos (huérfanos, cuarentena y borrado en Bunny)' },
    ],
  },

  // ── Eventos (2-sep-2026, ola 3 del Club adelantada) ───────────────────────
  // ADR rector: 2026-09-02-eventos-del-club-adr-rector-modelo-pared-horaria-
  // y-candados. Las acciones de PLATA van separadas de "gestionar" a propósito
  // (hallazgo del pase v3): aprobar un reembolso es una decisión financiera, y
  // el que escanea QRs en la puerta puede ser staff de campo sin acceso a nada
  // más. Sin defaults: tras `Sincronizar módulos`, Mariano tilda en `/permisos`.
  //
  // DOS módulos desde el 6-sep-2026 (decisión de Mariano; ADR
  // `2026-09-06-eventos-asistir-y-organizar-son-dos-entitlements-de-plan`, que
  // ENMIENDA la Decisión 6 del rector — "7 acciones en `eventos.*`" pasó a
  // 1 + 6 en dos módulos). Razón: "empresa que organiza" no existía como
  // concepto — el plan daba Eventos entero y ver/gestionar se tildaba por ROL,
  // así que la card de conectar Mercado Pago (rail de cobro, ADR 2026-09-05)
  // la veía TODA empresa con Eventos en el plan. Ahora:
  //   · `eventos`           = ASISTIR: lo que un miembro hace (vitrina, inscribirse,
  //                           comprar). Es el módulo con ruta y sidebar.
  //   · `eventos-organizar` = ORGANIZAR: lo que un operador hace. Ítem contratable
  //                           del plan SIN UI propia (molde `mercado-datos`): la
  //                           superficie sigue en `/eventos` y se gatea por acción de
  //                           ESTE módulo; conectar MP exige este módulo en el plan.
  // Organizar PRESUPONE asistir: el plan de un operador incluye los dos. El
  // código `eventos` NO se renombró (4 callables de miembro, colecciones y docs
  // lo usan); el nombre visible sí dice "Asistir".
  {
    codigo: 'eventos',
    nombre: 'Eventos · Asistir',
    descripcion: 'Eventos del Club para el miembro: ver la vitrina, inscribirse y comprar entradas de lo habilitado.',
    orden: 100,
    funciones: [
      { codigo: 'ver',                nombre: 'Ver la vitrina, inscribirse y comprar entradas de lo habilitado' },
    ],
  },
  {
    codigo: 'eventos-organizar',
    nombre: 'Eventos · Organizar',
    descripcion: 'Eventos del Club para el operador: crear y publicar eventos, entradas, check-in, stats, reembolsos y reseñas. Habilita conectar Mercado Pago.',
    orden: 101,
    funciones: [
      { codigo: 'gestionar-eventos',  nombre: 'Crear, editar, publicar y cancelar eventos, sus tipos de entrada y sus cupones de descuento' },
      { codigo: 'gestionar-acceso',   nombre: 'Invitar y otorgar acceso a eventos' },
      { codigo: 'hacer-checkin',      nombre: 'Escanear QRs y registrar asistencia en la puerta' },
      { codigo: 'ver-estadisticas',   nombre: 'Ver el embudo y las stats de los eventos propios' },
      { codigo: 'aprobar-reembolsos', nombre: 'Aprobar o rechazar solicitudes de reembolso' },
      // Los datos de contacto del asistente (ADR 2026-09-19 D3). Par propio y
      // no incluido en `hacer-checkin`: el staff de puerta usa la lista para
      // encontrar a alguien que perdió su QR, y para eso le alcanza el mail —
      // un teléfono no hace falta para dejar pasar a nadie. Nace sin asignar.
      { codigo: 'ver-datos-de-asistentes', nombre: 'Ver los datos de contacto que cargan los asistentes (teléfono, empresa, rubro)', naceApagada: true },
      { codigo: 'moderar-resenas',    nombre: 'Moderar y publicar calificaciones de asistentes' },
      // Conversión p1-5 (28-sep-2026, diseño v3 §25): ver PERSONAS en la
      // conversión — qué miembros miraron y no compraron (sólo los que dieron su
      // OK) y el recorrido de cada venta. Par propio y no parte de
      // `ver-estadisticas` (que es agregado): nace sin asignar, ni al dueño.
      { codigo: 'ver-interesados',    nombre: 'Ver qué miembros miraron un evento y no compraron (con su OK) y el recorrido de cada venta', naceApagada: true },
      // Inscriptos (29-sep-2026, ADR 2026-09-29 D7): la vista «Administración»
      // de la lista (montos por fila, descuentos, reembolsos, ingresos del
      // cabezal y la plata del CSV). Sin él, la vista «Ejecución»: el server no
      // manda ningún monto. La identidad (dueño/staff) lo tiene siempre; a los
      // roles de hoy se lo da `scripts/seed-permiso-ver-montos.cjs` (nadie
      // pierde lo que ve).
      { codigo: 'ver-montos',         nombre: 'Ver los montos en la lista de inscriptos (precios, descuentos, reembolsos e ingresos)' },
      // Inscriptos (29-sep-2026, ADR 2026-09-29 D2): marcar y desmarcar una
      // inscripción como PRUEBA (sale de ingresos, conversión y estadísticas;
      // sigue en la conciliación con su etiqueta). Lo cobrado sin devolver no
      // se marca, para nadie. Además del par, el colaborador tiene que
      // pertenecer al operador. Nace sin asignar: la identidad lo tiene siempre.
      { codigo: 'marcar-pruebas',     nombre: 'Marcar y desmarcar inscripciones de prueba (salen de ingresos, conversión y estadísticas)', naceApagada: true },
      // Marketing del evento (2-oct-2026, ADR 2026-10-02 D1): es parte de
      // ORGANIZAR, no un módulo ni un entitlement aparte. Tres pares y no uno
      // porque son tres riesgos distintos: cargar el kit (paleta, para quién,
      // hashtags, avisos) no saca nada afuera; aprobar una pieza es lo que la
      // deja salir a una página pública o a un paquete; pedirle textos a la IA
      // gasta cuota del organizador. Los tres nacen SIN asignar (§1.F, molde
      // `ver-interesados`): nadie los tiene —ni el dueño— hasta que se tildan
      // en /permisos (Wizard, por su short-circuit); el server los exige
      // ADEMÁS de la identidad de organizador (`autorizarOrganizador`), nunca
      // en su lugar. Un rol que hoy marca pruebas o ve montos no los hereda.
      { codigo: 'gestionar-marketing', nombre: 'Armar el kit de marketing del evento: paleta, audiencia, piezas y referidos', naceApagada: true },
      { codigo: 'aprobar-piezas',      nombre: 'Aprobar las piezas de marketing antes de que salgan a una página pública o a un paquete', naceApagada: true },
      { codigo: 'generar-con-ia',      nombre: 'Pedirle a la IA textos y ganchos nuevos para las piezas', naceApagada: true },
    ],
  },

  // ── Milbrands Operaciones (28-may-2026) ────────────────────────────────────
  // Subheader visual en el sidebar — agrupa items de organización interna de
  // la consultora (Admin, Documentación, y futuros items). Gating editable
  // por permiso en lugar de hardcodeo a esWizard: tras Sincronizar módulos,
  // Mariano tilda 'ver' en /permisos para Wizard + Operaciones (lo que él
  // decida). No hay defaults — sin el permiso, el subheader no se renderiza.
  {
    codigo: 'milbrandsOperaciones',
    nombre: 'Milbrands Operaciones',
    descripcion: 'Subheader del sidebar para items internos de la consultora (Admin, Docs, organización).',
    orden: 95,
    funciones: [
      { codigo: 'ver', nombre: 'Ver subheader y sus items' },
    ],
  },
  // ── Proyectos (jun-2026) — "nuestro Asana". Gating plan + permisos, cero
  // hardcode. Eje `operativo` desde el 31-oct-2026 (Mariano, report
  // LVyJbRGvZebhNU2MmO4G): la siembra le da sus acciones a los roles de cliente;
  // qué proyectos ve cada uno lo acota `firestore.rules` (`match /proyectos`).
  // Acciones de §9 del diseño (docs/modulo_proyectos.md); las que aún no tienen
  // UI quedan declaradas para poder asignarlas en /permisos cuando llegue su bloque.
  {
    codigo: 'proyectos',
    nombre: 'Proyectos',
    descripcion: 'Orquestación de proyectos: hitos, equipo, entregables y tareas propias (tareasProyecto).',
    orden: 96,
    funciones: [
      { codigo: 'ver',                 nombre: 'Ver proyectos (según scope)' },
      { codigo: 'crear',               nombre: 'Crear proyecto' },
      { codigo: 'editar',              nombre: 'Editar nombre/descripción/fechas/equipo' },
      { codigo: 'eliminar',            nombre: 'Eliminar (soft delete)' },
      { codigo: 'cerrar',              nombre: 'Cerrar (terminado/cancelado)' },
      { codigo: 'asignar_responsable', nombre: 'Cambiar responsable' },
      { codigo: 'ver_presupuesto',     nombre: 'Ver presupuesto' },
      { codigo: 'editar_presupuesto',  nombre: 'Editar presupuesto' },
      { codigo: 'aprobar_hito',        nombre: 'Marcar hito como cumplido' },
      { codigo: 'aprobar_entregable',  nombre: 'Aprobar/rechazar entregables' },
      { codigo: 'aprobar_horas',       nombre: 'Aprobar horas antes de facturar' },
      { codigo: 'compartir_externo',   nombre: 'Cambiar visibilidad / compartir con cliente' },
      { codigo: 'configurar',          nombre: 'Configurar tipos, estados, áreas, campos custom, plantillas' },
      { codigo: 'ver_dashboard',       nombre: 'Ver dashboard de supervisión' },
      { codigo: 'ver_postmortem',      nombre: 'Ver post-mortem' },
      { codigo: 'editar_postmortem',   nombre: 'Editar post-mortem' },
    ],
  },
  // ── Roadmap de desarrollo (17-jul-2026) — tablero Now/Next/Later del equipo
  // que construye CoWork. Interno de Milbrands (milbrandsInterno en su config).
  // Tras Sincronizar módulos, tildar 'ver' en /permisos para Wizard + Operaciones.
  // `responder` (tajada 5, desarrollo por proyectos) nace sin asignar: las
  // puertas exigen además staff y la identidad del wizard dentro del motor.
  {
    codigo: 'roadmap',
    nombre: 'Roadmap de desarrollo',
    descripcion: 'Tablero Now/Next/Later del equipo de desarrollo: horizontes, score, dependencias.',
    orden: 97,
    funciones: [
      { codigo: 'ver',        nombre: 'Ver el roadmap' },
      { codigo: 'crear',      nombre: 'Crear items' },
      { codigo: 'editar',     nombre: 'Editar items (título, tipo, tamaño, impacto/confianza, responsable)' },
      { codigo: 'mover',      nombre: 'Cambiar horizonte y estado' },
      { codigo: 'responder',  nombre: 'Responder y comentar a una licencia en Desarrollo', naceApagada: true },
      { codigo: 'priorizar',  nombre: 'Fijar / reordenar a mano el backlog' },
      { codigo: 'eliminar',   nombre: 'Eliminar items' },
      { codigo: 'configurar', nombre: 'Configurar los catálogos (tipos, responsables)' },
    ],
  },
]

// La función tal como se guarda en `modulos`: `naceApagada` es una decisión
// del catálogo para la siembra de roles, no un dato del doc. Sólo viajaría a
// las funciones nuevas (las existentes no se reescriben), y una copia a medias
// en Firestore contradiría al catálogo.
export function funcionParaFirestore({ naceApagada: _, ...f }) {
  return { ...f, activo: true }
}

/**
 * Sincroniza MODULOS_CATALOGO con la colección Firestore `modulos`.
 * - Crea módulos que falten.
 * - Agrega funciones nuevas a módulos existentes (nunca borra).
 * - NO pisa: activo, orden, nombre, descripcion (los controla el admin en el ABM).
 *
 * @param {Object} opts
 * @param {boolean} [opts.dryRun=false]
 * @returns {Promise<{creados: string[], funcionesAgregadas: Array<{modulo: string, funcion: string}>}>}
 */
export async function sincronizarModulosConFirestore(opts = {}) {
  const { dryRun = false } = opts
  console.groupCollapsed(`[sincronizarModulos] dryRun=${dryRun}`)

  const snap = await getDocs(collection(db, COLLECTIONS.MODULOS))
  const existentesPorCodigo = new Map()
  snap.forEach(d => {
    const data = d.data()
    if (data.codigo) existentesPorCodigo.set(data.codigo, docConId(d))
  })

  const creados = []
  const funcionesAgregadas = []

  const ejesBackfilled = []

  for (const mod of MODULOS_CATALOGO) {
    const existente = existentesPorCodigo.get(mod.codigo)
    // Eje default desde la tabla de fallback. El admin lo puede cambiar
    // desde "Items del sidebar" sin redeploy.
    const ejeDefault = ejeDeFallback(mod.codigo)
    if (!existente) {
      const payload = {
        codigo: mod.codigo,
        nombre: mod.nombre,
        descripcion: mod.descripcion,
        orden: mod.orden,
        eje: ejeDefault,
        activo: true,
        esSistema: true,
        funciones: mod.funciones.map(funcionParaFirestore),
        creadoEn: serverTimestamp(),
        actualizadoEn: serverTimestamp(),
      }
      if (!dryRun) {
        const ref = await addDoc(collection(db, COLLECTIONS.MODULOS), payload)
        console.log(`[sincronizarModulos] creado: ${mod.codigo} (eje=${ejeDefault}) → id=${ref.id}`)
      } else {
        console.log(`[sincronizarModulos] PLANEADO crear: ${mod.codigo} (eje=${ejeDefault})`)
      }
      creados.push(mod.codigo)
      continue
    }

    // Módulo existe — verificar si faltan funciones o el campo `eje`.
    const fnExistentes = new Set((existente.funciones || []).map(f => f.codigo))
    const fnAAgregar = mod.funciones.filter(f => !fnExistentes.has(f.codigo))
    const necesitaBackfillEje = !existente.eje

    if (fnAAgregar.length > 0 || necesitaBackfillEje) {
      const patch = { actualizadoEn: serverTimestamp() }
      if (fnAAgregar.length > 0) {
        patch.funciones = [
          ...(existente.funciones || []),
          ...fnAAgregar.map(funcionParaFirestore),
        ]
      }
      if (necesitaBackfillEje) {
        patch.eje = ejeDefault
        ejesBackfilled.push({ modulo: mod.codigo, eje: ejeDefault })
        console.log(`[sincronizarModulos] ${mod.codigo} eje backfill=${ejeDefault}`)
      }
      if (!dryRun) {
        await updateDoc(doc(db, COLLECTIONS.MODULOS, existente.id), patch)
      }
      for (const f of fnAAgregar) {
        funcionesAgregadas.push({ modulo: mod.codigo, funcion: f.codigo })
        console.log(`[sincronizarModulos] ${mod.codigo} + fn ${f.codigo}`)
      }
    }
  }

  console.log(`[sincronizarModulos] creados=${creados.length} funcionesAgregadas=${funcionesAgregadas.length} ejesBackfilled=${ejesBackfilled.length}`)
  console.groupEnd()

  // Después de sincronizar, refrescamos el cache local de ejes (lo va a
  // consultar ejeDe() para que la UI vea inmediatamente lo que quedó en DB).
  if (!dryRun) {
    try { await cargarEjesEnCache() } catch (e) {
      console.warn('[sincronizarModulos] no se pudo refrescar cache de ejes:', e?.message)
    }
  }

  return { creados, funcionesAgregadas, ejesBackfilled }
}

/**
 * Lee /modulos y llena el cache de ejes que consulta `ejeDe()`.
 * Llamar al bootstrap (después del login) y después de editar un eje desde
 * la UI "Items del sidebar".
 */
export async function cargarEjesEnCache() {
  const snap = await getDocs(collection(db, COLLECTIONS.MODULOS))
  const docs = snap.docs.map(d => d.data())
  setEjesCache(docs)
  return docs.length
}

/**
 * Persiste un cambio de eje en /modulos/{id} y refresca el cache local.
 * @param {string} moduloId — el id del doc en Firestore (no el código).
 * @param {string} moduloCodigo — el código del módulo (para el cache).
 * @param {'operativo'|'plataforma'} eje
 */
export async function setEjeDeModulo(moduloId, moduloCodigo, eje) {
  if (!['operativo', 'plataforma'].includes(eje)) {
    throw new Error(`eje inválido: ${eje}`)
  }
  await updateDoc(doc(db, COLLECTIONS.MODULOS, moduloId), {
    eje,
    actualizadoEn: serverTimestamp(),
  })
  setEjeCache(moduloCodigo, eje)
}

/**
 * Carga el catálogo completo desde Firestore (para la UI del ABM).
 * Devuelve ordenado por `orden` asc.
 */
export async function cargarModulosDesdeFirestore() {
  const snap = await getDocs(collection(db, COLLECTIONS.MODULOS))
  const modulos = snap.docs.map(docConId)
  modulos.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  return modulos
}

// Exposición en window para admin (consola)
if (typeof window !== 'undefined') {
  window.__sincronizarModulos = sincronizarModulosConFirestore
  window.__cargarModulos = cargarModulosDesdeFirestore
}
